import { OverviewSearchReelModel } from "./../models/overview-search-reel";
import { ReelItemModel, ReelItemType } from "./../models/reel-item";
import { autoinject, Container } from "aurelia-dependency-injection";
import { backboneViewProperties } from "../../decorators/backbone-properties";
import { OverviewReelModel } from "../models/overview-reel";
import { ReelListView } from "./partials/reel-list";
import { prop } from "../../decorators/property";
import { ThinkerView } from "../../shared/views/thinker";
import { AppEvents } from "../../app-events";
import { ContainerHelper } from "../../shared/helpers/container-helper";

const template = require<string>("./overview-reel.html");

declare var Beacon: any;

const dom = {
    REEL_LIST_REGION: ".reel-list-region",
    REEL_BUTTONS: ".js-reel-button",
    REEL_BUTTON_NEXT: ".reel-button-next",
    REEL_BUTTON_PREV: ".reel-button-prev",
    REEL_LIST_ITEMS: ".js-reel-list-item",
    REEL_LIST: ".js-reel-list",
    RUN_SEARCH: ".js-my-justis-run-search",
    EMAIL_LINK: ".training-email",
    HELP_LINK: ".help-link",
    PROFICIENCY_LINK: ".proficiency-link"
};

@autoinject
@backboneViewProperties({
    className: "reel js-reel",
    tagName: "div",
    template: template
})
export class OverviewReelView extends Marionette.LayoutView<OverviewReelModel> {

    @prop() public Title: string;
    @prop() public Query: string;
    @prop() public Type: string;

    private reelListView: ReelListView;
    private reelListRegion: Marionette.Region;
    private resizeTimeout;
    private appEvents: Backbone.Events;

    constructor(options) {
        super(options);
        this.setupModelListeners();
        this.appEvents = ContainerHelper.Events;
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events[`click ${dom.REEL_BUTTON_NEXT}`] = (e) => this.reelButtonNextClicked(e);
        events[`click ${dom.REEL_BUTTON_PREV}`] = (e) => this.reelButtonPrevClicked(e);
        events[`click ${dom.RUN_SEARCH}`] = (e) => this.runSearchClick(e);
        events[`click ${dom.EMAIL_LINK}`] = (e) => this.trainingEmailClick(e);
        events[`click ${dom.HELP_LINK}`] = (e) => this.helpLinkClick(e);
        events[`click ${dom.PROFICIENCY_LINK}`] = (e) => this.proficiencyLinkClick(e);
        return events;
    }

    public regions(): any {
        return {
            reelListRegion: dom.REEL_LIST_REGION
        };
    }

    public onShow() {
        this.$el.addClass(this.model.Type);
        this.reelListView = new ReelListView({ collection: this.model.Items });
        this.setupWindowResizeListener();
        this.reelListRegion.show(this.reelListView);
        this.model.loadData();
        this.updateDisplay();
    }

    private setupModelListeners() {
        this.listenTo(this.model, "updateDisplay", this.updateDisplay);
        this.listenTo(this.model, "change:SlideStart", this.slideStartChanged);
        this.listenTo(this.model, "change:SearchQuery", this.searchQueryChanged);
    }

    private slideStartChanged(model, property) {
        const moveTo = -((this.getItemWidth() + 15) * property);
        const $reelList = this.$(dom.REEL_LIST);
        const transformString = `translateX(${moveTo}px)`;
        $reelList.css({transform: transformString});
        this.updateButtons();
    }

    private trainingEmailClick(e: JQueryEventObject) {
        e.stopPropagation();
        this.appEvents.trigger(AppEvents.AppInsightsMyJustisTrainingEmailClicked, {});
    }

    private helpLinkClick(e: JQueryEventObject) {
        e.stopPropagation();
        e.preventDefault();
        const $target = $(e.currentTarget);
        const article = $target.attr("data-article");
        Beacon("close");
        Beacon("navigate", "/");
        Beacon("article", article);
        this.appEvents.trigger(AppEvents.AppInsightsMyJustisCoverageClicked, {});
    }

    private proficiencyLinkClick(e: JQueryEventObject) {
        e.stopPropagation();
        this.appEvents.trigger(AppEvents.AppInsightsMyJustisGetCertified, {});
    }

    private runSearchClick(e: JQueryEventObject) {
        e.preventDefault();
        e.stopPropagation();
        (this.model as OverviewSearchReelModel).triggerSearch();
        this.appEvents.trigger(AppEvents.AppInsightsMyJustisRunLastSearchResult, {});
    }

    private clearPlaceholders() {
        this.model.Items.remove(this.model.Items.where({ItemType: ReelItemType.Placeholder}));
    }

    private addPlaceholders() {
        const slots = this.getAvailableSlots();
        while (this.model.Items.size() < slots) {
            this.model.Items.add(new ReelItemModel({ItemType: ReelItemType.Placeholder}));
        }
    }

    private updateDisplay() {
        this.clearPlaceholders();
        if (this.model.Items.size() === 0) {
            this.$el.hide();
        } else {
            this.addPlaceholders();
            this.setWidth();
            this.$el.show();
        }
    }

    private searchQueryChanged() {
        this.$(dom.RUN_SEARCH).text(this.model.SearchQuery);
    }

    private setWidth(): void {
        const $reelItems = this.$(dom.REEL_LIST_ITEMS);
        const itemWidth = this.getItemWidth();
        $reelItems.width(itemWidth);
        this.updateButtons();
    }

    private getItemWidth() {
        const slots = this.getAvailableSlots();
        const containerWidth = this.$(dom.REEL_LIST_REGION).width();
        const $reelItems = this.$(dom.REEL_LIST_ITEMS);
        const itemWidth = ((containerWidth + 15) / slots) - 15;
        return itemWidth;
    }

    private getAvailableSlots() {
        const minSlots = 4;
        const $reelRegion = this.$(dom.REEL_LIST_REGION);
        const totalWidth = $reelRegion.outerWidth() + 15;
        const maxSlots = Math.floor(totalWidth / 265);
        if (this.model.Items.size() >= maxSlots) {
            return maxSlots;
        } else {
            const slots = Math.min(minSlots, maxSlots);
            return slots;
        }
    }

    private reelButtonNextClicked(e: JQueryMouseEventObject) {
        const $target = $(e.currentTarget);
        if ($target.hasClass("disabled")) {
            return;
        }
        const slots = this.getAvailableSlots();
        const after = this.model.Items.size() - (this.model.SlideStart + slots);
        this.model.SlideStart = this.model.SlideStart + Math.min(after, slots);
    }

    private reelButtonPrevClicked(e: JQueryMouseEventObject) {
        const $target = $(e.currentTarget);
        if ($target.hasClass("disabled")) {
            return;
        }
        const slots = this.getAvailableSlots();
        const before = this.model.SlideStart;
        this.model.SlideStart = this.model.SlideStart - Math.min(before, slots);
    }

    private updateButtons() {
        const contentAfter = (this.model.Items.size() - this.model.SlideStart) > this.getAvailableSlots();
        const contentBefore = this.model.SlideStart > 0;
        this.$(".reel-button-next").toggleClass("disabled", !contentAfter);
        this.$(".reel-button-prev").toggleClass("disabled", !contentBefore);
    }

    private setupWindowResizeListener() {
        $(window).resize(() => {
            this.updateDisplay();
            this.slideStartChanged(this.model, this.model.SlideStart);
        });
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/my-justis/views/overview-reel.ts